import { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { getMeasurements, createMeasurement, deleteMeasurement } from '../../api/measurements';
import { getParcels } from '../../api/parcels';
import Button from '../../components/common/Button';
import Input, { Select } from '../../components/common/Input';
import Card from '../../components/common/Card';
import Modal from '../../components/common/Modal';
import { FullPageSpinner } from '../../components/common/Spinner';

export default function Measurements() {
  const [measurements, setMeasurements] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterParcel, setFilterParcel] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ parcel: '', soil_moisture: '', ph_value: '', soil_temperature: '' });

  useEffect(() => {
    Promise.all([getMeasurements(), getParcels()])
      .then(([mRes, pRes]) => {
        setMeasurements(mRes.data?.results ?? mRes.data);
        setParcels(pRes.data?.results ?? pRes.data);
      })
      .catch(() => toast.error('Неуспешно вчитување на податоците'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filterParcel
    ? measurements.filter((m) => String(m.parcel) === filterParcel)
    : measurements;

  const chartData = [...filtered]
    .slice(0, 30)
    .reverse()
    .map((m) => ({
      date: format(new Date(m.measured_at), 'MM/dd HH:mm'),
      moisture: parseFloat(m.soil_moisture ?? 0),
      ph: parseFloat(m.ph_value ?? 0),
      temp: parseFloat(m.soil_temperature ?? 0),
    }));

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await createMeasurement(form);
      setMeasurements((m) => [res.data, ...m]);
      toast.success('Мерењето е додадено');
      setShowModal(false);
      setForm({ parcel: '', soil_moisture: '', ph_value: '', soil_temperature: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Неуспешно додавање на мерење');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMeasurement(id);
      setMeasurements((m) => m.filter((x) => x.id !== id));
      toast.success('Мерењето е избришано');
    } catch {
      toast.error('Неуспешно бришење');
    }
  };

  if (loading) return <FullPageSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Мерења</h1>
          <p className="text-gray-500 text-sm mt-1">Очитувања на влажноста на почвата, pH и температурата</p>
        </div>
        <Button onClick={() => setShowModal(true)}>+ Додај мерење</Button>
      </div>

      <Card>
        <div className="flex items-center gap-4 mb-5">
          <Select
            label="Филтрирај по парцела"
            value={filterParcel}
            onChange={(e) => setFilterParcel(e.target.value)}
            className="w-56"
          >
            <option value="">Сите парцели</option>
            {parcels.map((p) => (
              <option key={p.id} value={String(p.id)}>{p.parcel_name}</option>
            ))}
          </Select>
          <span className="text-sm text-gray-400 mt-5">{filtered.length} записи</span>
        </div>

        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="moisture" stroke="#16a34a" name="Влажност %" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="ph" stroke="#3b82f6" name="pH" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="temp" stroke="#f59e0b" name="Темп °C" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['Датум', 'Парцела', 'Влажност %', 'pH', 'Темп °C', ''].map((h, i) => (
                <th key={i} className="text-left px-5 py-3 text-gray-500 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-10 text-gray-400">
                  Нема пронајдени мерења.
                </td>
              </tr>
            ) : (
              filtered.map((m) => (
                <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-5 py-3 text-xs text-gray-500">
                    {format(new Date(m.measured_at), 'MMM dd, yyyy HH:mm')}
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {parcels.find((p) => p.id === m.parcel)?.parcel_name || `Парцела #${m.parcel}`}
                  </td>
                  <td className="px-5 py-3">{m.soil_moisture ?? '—'}</td>
                  <td className="px-5 py-3">{m.ph_value ?? '—'}</td>
                  <td className="px-5 py-3">{m.soil_temperature ?? '—'}</td>
                  <td className="px-5 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(m.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      Избриши
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Додај мерење">
        <form onSubmit={handleCreate} className="space-y-4">
          <Select
            label="Парцела"
            name="parcel"
            value={form.parcel}
            onChange={handleChange}
            required
          >
            <option value="">Изберете парцела...</option>
            {parcels.map((p) => (
              <option key={p.id} value={p.id}>{p.parcel_name}</option>
            ))}
          </Select>
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Влажност на почвата %"
              name="soil_moisture"
              type="number"
              step="0.1"
              value={form.soil_moisture}
              onChange={handleChange}
              required
              placeholder="45.2"
            />
            <Input
              label="pH"
              name="ph_value"
              type="number"
              step="0.1"
              min="0"
              max="14"
              value={form.ph_value}
              onChange={handleChange}
              required
              placeholder="6.5"
            />
            <Input
              label="Температура °C"
              name="soil_temperature"
              type="number"
              step="0.1"
              value={form.soil_temperature}
              onChange={handleChange}
              required
              placeholder="22.3"
            />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Откажи</Button>
            <Button type="submit" loading={saving}>Зачувај</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
